import { defineMessages, useIntl } from 'react-intl';

import type {
	FlagDescriptor,
	FlagsStateContainer,
} from '@confluence/flags/entry-points/FlagsStateContainer.ts';

const ENFORCE_DSP_LEARN_MORE_LINK =
	'https://support.atlassian.com/security-and-access-policies/docs/what-is-a-data-security-policy/';

const i18n = defineMessages({
	errorIconLabel: {
		id: 'bulk-permissions.flag-message.error-icon-label',
		defaultMessage: 'Error',
		description:
			'Accessibility label for the error icon displayed in flag notifications when bulk operations fail',
	},
	processingFlagTitle: {
		id: 'bulk-permissions.flag-message.processing',
		defaultMessage: 'Bulk change processing',
		description:
			'Title text for the flag notification indicating that bulk role or permission changes are being processed',
	},
	processingFlagContent: {
		id: 'bulk-permissions.flag-message.processing-content',
		defaultMessage: 'This can take a while. We’ll notify you by email when it’s finished.',
		description: 'The content for the processing flag',
	},
	errorFlagTitle: {
		id: 'bulk-permissions.flag-message.error',
		defaultMessage: 'Bulk access change failed',
		description: 'Title for the error flag displayed when bulk access change fails',
	},
	errorFlagContent: {
		id: 'bulk-permissions.flag-message.error-content',
		defaultMessage: 'We ran into a problem completing your bulk access change. Try again.',
		description:
			'Body text shown in an error notification flag when a bulk access change operation fails, prompting the user to try again.',
	},
	warningTitle: {
		id: 'bulk-permissions.flag-message.warning-title',
		defaultMessage: 'Policy prohibits external sharing',
		description: 'Warning title when a data security policy prohibits external sharing',
	},
	warningMessage: {
		id: 'bulk-permissions.flag-message.warning-message',
		defaultMessage:
			"Even if a role includes permission to enable anonymous access or public links, your organization's security policy will prevent it.",
		description: 'Warning message when a data security policy prohibits external sharing',
	},
	learnMore: {
		id: 'bulk-permissions.flag-messagelearn-more',
		defaultMessage: 'Learn about policies',
		description: 'Label for a link to learn more about data security policies',
	},
});

export const useBulkRoleFlags = ({
	flags,
}: {
	flags: FlagsStateContainer;
}): {
	showErrorFlag: () => Promise<FlagDescriptor>;
	showProcessingFlag: () => Promise<FlagDescriptor>;
	showInfoFlag: () => Promise<FlagDescriptor>;
} => {
	const { formatMessage } = useIntl();

	const showErrorFlag = (): Promise<FlagDescriptor> =>
		flags.showErrorFlag({
			id: `error-${Date.now()}`,
			title: formatMessage(i18n.errorFlagTitle),
			description: formatMessage(i18n.errorFlagContent),
			isAutoDismiss: true,
		});

	const showProcessingFlag = (): Promise<FlagDescriptor> =>
		flags.showInfoFlag({
			id: `processing-${Date.now()}`,
			title: formatMessage(i18n.processingFlagTitle),
			description: formatMessage(i18n.processingFlagContent),
			isAutoDismiss: true,
		});

	const showInfoFlag = (): Promise<FlagDescriptor> =>
		flags.showInfoFlag({
			id: `processing-${Date.now()}`,
			title: formatMessage(i18n.warningTitle),
			description: formatMessage(i18n.warningMessage),
			actions: [
				{
					content: formatMessage(i18n.learnMore),
					href: ENFORCE_DSP_LEARN_MORE_LINK,
				},
			],
			isAutoDismiss: true,
		});

	return {
		showErrorFlag,
		showProcessingFlag,
		showInfoFlag,
	};
};
